import React, { useEffect, useMemo, useRef, useState } from "react";

function getSpeechRecognitionCtor() {
  if (typeof window === "undefined") return null;
  return (
    window.SpeechRecognition ||
    window.webkitSpeechRecognition ||
    window.mozSpeechRecognition ||
    window.msSpeechRecognition ||
    null
  );
}

export function useSpeechRecognition({ onFinal, lang = "en-US" } = {}) {
  const [isSupported, setIsSupported] = useState(false);
  const [listening, setListening] = useState(false);
  const [interim, setInterim] = useState("");
  const [error, setError] = useState(null);
  const recognitionRef = useRef(null);
  const onFinalRef = useRef(onFinal);

  useEffect(() => {
    onFinalRef.current = onFinal;
  }, [onFinal]);

  useEffect(() => {
    const Ctor = getSpeechRecognitionCtor();
    if (!Ctor) {
      setIsSupported(false);
      return;
    }
    setIsSupported(true);

    let recognition;
    try {
      recognition = new Ctor();
    } catch (e) {
      setIsSupported(false);
      return;
    }

    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.lang = lang;

    recognition.onresult = (event) => {
      let interimText = "";
      let finalText = "";
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const result = event.results[i];
        const transcript = result[0]?.transcript || "";
        if (result.isFinal) {
          finalText += transcript;
        } else {
          interimText += transcript;
        }
      }
      setInterim(interimText);
      if (finalText) {
        onFinalRef.current?.(finalText);
      }
    };

    recognition.onerror = (event) => {
      if (event.error === "no-speech" || event.error === "aborted") {
        setListening(false);
        return;
      }
      setError(`Speech error: ${event.error}`);
      setListening(false);
    };

    recognition.onend = () => {
      setListening(false);
      setInterim("");
    };

    recognition.onstart = () => {
      setListening(true);
      setError(null);
      setInterim("");
    };

    recognitionRef.current = recognition;

    return () => {
      try {
        recognition.onresult = null;
        recognition.onerror = null;
        recognition.onend = null;
        recognition.onstart = null;
        if (recognition.abort) recognition.abort();
      } catch (_e) {
        /* ignore */
      }
      recognitionRef.current = null;
    };
  }, [lang]);

  const start = () => {
    const r = recognitionRef.current;
    if (!r) return;
    try {
      r.start();
    } catch (_e) {
      /* ignore if already started */
    }
  };

  const stop = () => {
    const r = recognitionRef.current;
    if (!r) return;
    try {
      r.stop();
    } catch (_e) {
      /* ignore */
    }
  };

  const toggle = () => {
    if (listening) stop();
    else start();
  };

  return { isSupported, listening, interim, error, start, stop, toggle };
}

function capitalizeFirst(text) {
  if (!text) return "";
  const trimmed = text.replace(/\s+$/g, " ");
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
}

export default function SpeechInput({
  value,
  onChange,
  as = "input",
  className = "",
  inputClassName = "",
  buttonClassName = "",
  placeholder,
  lang = "en-US",
  disabled,
  type,
  name,
  rows,
  inputMode,
  required,
  inputRef,
  ...restProps
}) {
  const handleFinal = (transcript) => {
    const clean = capitalizeFirst(transcript);
    const next = value ? `${value.replace(/\s?$/, " ")}${clean.trim()}` : clean.trim();
    onChange?.({ target: { name, value: next } });
  };

  const speech = useSpeechRecognition({ onFinal: handleFinal, lang });

  const Tag = as;
  const showMic = speech.isSupported && !disabled && (as === "input" || as === "textarea");

  const commonProps = {
    name,
    value,
    onChange,
    placeholder:
      placeholder + (speech.isSupported && !disabled ? " (or tap 🎙️ to dictate)" : ""),
    disabled,
    required,
    ref: inputRef,
    ...restProps,
  };

  const groupClasses = useMemo(() => {
    const parts = ["input-group", className].filter(Boolean);
    if (as === "textarea") {
      parts.push("speech-input-textarea-group");
    }
    return parts.join(" ");
  }, [className, as]);

  const wrapClasses = useMemo(() => {
    return as === "textarea" ? "w-100" : "flex-grow-1";
  }, [as]);

  if (!showMic) {
    const tagProps = { ...commonProps, className: [inputClassName, "form-control"].filter(Boolean).join(" ") };
    if (Tag === "input") tagProps.type = type || "text";
    if (Tag === "input" && inputMode) tagProps.inputMode = inputMode;
    if (Tag === "textarea" && rows) tagProps.rows = rows;
    return <Tag {...tagProps} />;
  }

  const InputTag = (
    <Tag
      {...commonProps}
      className={[
        inputClassName,
        "form-control",
        speech.listening ? "border-primary" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      type={Tag === "input" ? type || "text" : undefined}
      inputMode={Tag === "input" ? inputMode : undefined}
      rows={Tag === "textarea" ? rows : undefined}
    />
  );

  return (
    <div className={groupClasses}>
      {as === "textarea" ? (
        <div className="w-100 d-flex flex-column gap-2">
          {InputTag}
          <div className="d-flex justify-content-between align-items-center">
            <div className="small text-muted min-h-1-line">
              {speech.listening && speech.interim
                ? <>🎙️ <em>{speech.interim}</em></>
                : null}
              {speech.error ? <span className="text-danger">{speech.error}</span> : null}
            </div>
            <button
              type="button"
              className={
                speech.listening
                  ? "btn btn-sm btn-danger"
                  : "btn btn-sm btn-outline-secondary"
              }
              onClick={speech.toggle}
              disabled={disabled}
              title={speech.listening ? "Stop dictation" : "Dictate with voice"}
            >
              {speech.listening ? "⏹ Stop" : "🎙️ Dictate"}
            </button>
          </div>
        </div>
      ) : (
        <>
          <span className={wrapClasses}>
            {InputTag}
            {speech.listening && speech.interim ? (
              <div className="form-text fst-italic text-primary small">
                🎙️ {speech.interim}
              </div>
            ) : null}
            {speech.error ? (
              <div className="form-text text-danger small">{speech.error}</div>
            ) : null}
          </span>
          <button
            type="button"
            className={
              speech.listening
                ? "btn btn-outline-danger"
                : "btn btn-outline-secondary"
            }
            onClick={speech.toggle}
            disabled={disabled}
            title={speech.listening ? "Stop dictation" : "Dictate with voice"}
          >
            {speech.listening ? "⏹" : "🎙️"}
          </button>
        </>
      )}
    </div>
  );
}
